import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent, Checkbox, CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid, Link, List,
  Typography
} from '@mui/material'
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl'
import { type OrganizationBasicInformation, sloListFilter } from 'services'
import { ButtonWithIcon, cardStyles, formatDate, useGlobalMessageHandler } from 'components'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import AddIcon from '@mui/icons-material/Add'
import IconButton from '@mui/material/IconButton'
import ListItem from '@mui/material/ListItem'
import RemoveIcon from '@mui/icons-material/Remove'
import { auditOrganization } from 'services'
import { formatErrorFieldMessages } from 'interfaces'
import dayjs from 'dayjs'
import { useSessionContext } from 'session'

interface OrganizationAuditProps {
  originalBasicInfo: OrganizationBasicInformation
  updateLastAudited: (lastAudited: string) => void
  loading: boolean
}
enum AlertType {
  INFO = 'info',
  ERROR = 'error',
  WARNING = 'warning',
  SUCCESS = 'success',
}

interface AlertInfo {
  severity: AlertType
  message: string
}

const alerts: { [key in AlertType]: AlertInfo } = {
  [AlertType.INFO]: { severity: AlertType.INFO, message: 'The organization has no matching SLO' },
  [AlertType.ERROR]: { severity: AlertType.ERROR, message: 'The organization is currently not meeting SLO' },
  [AlertType.WARNING]: { severity: AlertType.WARNING, message: 'The organization is almost out of SLO compliance' },
  [AlertType.SUCCESS]: { severity: AlertType.SUCCESS, message: 'This organization is within SLO compliance' }
}

export const OrganizationAuditBlock: React.FC<OrganizationAuditProps> = ({ originalBasicInfo, loading, updateLastAudited }) => {
  const [expanded, setExpanded] = useState(false)
  const [loadingClick, setLoadingClick] = useState(false)
  const [openAuditDialog, setOpenAuditDialog] = React.useState(false)
  const [auditedFields, setAuditedFields] = React.useState(false)
  const [loadingSlo, setLoadingSlo] = React.useState(true)
  const [alertType, setAlertType] = React.useState<AlertInfo>(alerts[AlertType.INFO])
  const { openNewTab } = useSessionContext()

  const { showSuccessMessage, showErrorMessage } = useGlobalMessageHandler()

  useEffect(() => {
    const fetchSlo = async (): Promise<void> => {
      setAlertType(alerts[AlertType.INFO])
      setLoadingSlo(true)

      if (originalBasicInfo.orgTypeId == null || originalBasicInfo.country?.id == null || originalBasicInfo.lastAudited == null) {
        return
      }

      const slosSameCountryAndType = await sloListFilter({
        OrgType: originalBasicInfo.orgTypeId,
        Country: originalBasicInfo.country.id
      })

      if (slosSameCountryAndType.data.length === 0) return

      const daysToSloCutoff = slosSameCountryAndType.data[0].days ?? 0
      const dayLastAudit = dayjs(originalBasicInfo.lastAudited)
      const daySloCutoff = dayLastAudit.add(daysToSloCutoff, 'days')

      if (daySloCutoff.isBefore(dayjs())) {
        setAlertType(alerts[AlertType.ERROR])
      } else if (daySloCutoff.diff(dayjs(), 'days') <= 7) {
        setAlertType(alerts[AlertType.WARNING])
      } else {
        setAlertType(alerts[AlertType.SUCCESS])
      }
    }

    fetchSlo().finally(() => { setLoadingSlo(false) })
  }, [originalBasicInfo.orgTypeId, originalBasicInfo.country?.id, originalBasicInfo.lastAudited])

  const toggleLoadingClick = (): void => {
    setLoadingClick(prevState => !prevState)
  }
  const handleOpenAuditDialog = (): void => {
    setOpenAuditDialog(true)
    setAuditedFields(false)
    setExpanded(false)
  }

  const handleCloseAuditDialog = (): void => {
    setOpenAuditDialog(false)
    setAuditedFields(false)
    setExpanded(false)
  }

  const handleAuditOrganization = (): void => {
    setLoadingClick(true)
    auditOrganization(originalBasicInfo.id).then(value => {
      if (value.succeeded && (value.result != null)) {
        showSuccessMessage('Organization audited')
        updateLastAudited(value.result.lastAudited)
        handleCloseAuditDialog()
      } else {
        showErrorMessage(formatErrorFieldMessages(value.errors))
      }
    }).catch(() => {
      showErrorMessage('Failed to audit Organization')
    }).finally(() => {
      toggleLoadingClick()
    })
  }

  return (
      <Card sx={cardStyles}>

        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={4.5} container flexDirection={'row'} alignItems="center">
              <ChecklistRtlIcon/>
              <Typography pl={1}>{`Last Audited: ${formatDate(originalBasicInfo.lastAudited ?? '')}`}</Typography>
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              {!loadingSlo && <Alert severity={alertType.severity}>  {alertType.message}</Alert>}
              {loadingSlo && <CircularProgress/>}
            </Grid>
            <Grid item xs={4.5} container justifyContent="end">
              <ButtonWithIcon variant="contained" size='large' nameButton='I have completed an audit'
                              icon={<ChecklistRtlIcon/>} onClick={handleOpenAuditDialog}></ButtonWithIcon>
              <Dialog
                  open={openAuditDialog}
                  onClose={handleCloseAuditDialog}
                  maxWidth={'lg'}
                  scroll={'body'}
              >
                <DialogTitle>
                  {'Check all required audited fields'}
                </DialogTitle>
                <DialogContent>
                  <FormControl
                      required
                      error={!auditedFields}
                      component="fieldset"
                      sx={{ m: 3 }}
                      variant="standard"
                  >
                    <FormGroup>
                      <FormControlLabel
                          control={
                            <Checkbox checked={auditedFields} onChange={() => { setAuditedFields(prevState => !prevState) }} name="Audited Fields" />
                          }
                          label="In ensuring a complete audit, I have reviewed the basic information, addresses, phone numbers, URLs, sources, positions, and contact information for all positions in this organization."
                      />
                      <Box pt={1} display={'flex'} flexDirection={'row'} alignItems={'center'}>
                        <Typography variant="subtitle2">Expand to view the criteria for a complete audit</Typography>
                        <IconButton size={'small'} onClick={() => { setExpanded(prevState => !prevState) }}>{expanded ? <RemoveIcon/> : <AddIcon/> }</IconButton>
                      </Box>
                      {expanded && <Box display={'flex'} justifyContent={'center'}>
                        <List sx={{ listStyleType: 'disc', columns: 2 }}>
                          <ListItem sx={{ display: 'list-item' }}>Basic information (name, local name, logo image, country, parent, type)</ListItem>
                          <ListItem sx={{ display: 'list-item' }}>Addresses and phone numbers</ListItem>
                          <ListItem sx={{ display: 'list-item' }}>URLs</ListItem>
                          <ListItem sx={{ display: 'list-item' }}>Sources</ListItem>
                          <ListItem sx={{ display: 'list-item' }}>
                            <Link component="button" underline={'hover'} onClick={() => {
                              if (originalBasicInfo.id != null) { openNewTab(`/positions/${originalBasicInfo.id}`) }
                            }}>
                            Positions
                            </Link>
                          </ListItem>
                          <ListItem sx={{ display: 'list-item' }}>Position address and phone numbers</ListItem>
                        </List>
                      </Box>}
                    </FormGroup>
                  </FormControl>
                </DialogContent>
                <DialogActions>
                  {loadingClick
                    ? <CircularProgress/>
                    : <>
                  <Button onClick={handleCloseAuditDialog}>Cancel</Button>
                  <Button onClick={handleAuditOrganization} disabled={!auditedFields} autoFocus>Audit</Button>
                      </>
                }
                </DialogActions>
              </Dialog>
            </Grid>
          </Grid>

        </CardContent>
      </Card>
  )
}
